import { useEffect, useState, useCallback, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { PageHeader, PillBadge, GhostButton } from "../components/Primitives";
import { MapPin, RefreshCw, Check, ExternalLink, Radio, Download } from "lucide-react";
import { SortableHeader, useSortableRows } from "../components/SortableTable";
import { toast } from "sonner";
import { NewDispatchModal } from "../components/dispatch/NewDispatchModal";

const POLL_MS = 60000;

const relTime = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const fmtDuration = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return rs ? `${m}m ${rs}s` : `${m}m`;
};

export default function Geofence() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // open | closed | all
  // Entity filters — applied client-side over the already-enriched rows so
  // dispatchers get instant feedback without a re-fetch on every change.
  const [guardFilter, setGuardFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  // When set, opens the dispatch modal restricted to the breach's site.
  const [dispatchFor, setDispatchFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = statusFilter && statusFilter !== "all" ? { status: statusFilter } : {};
      const r = await api.get("/geofence/breaches", { params });
      setRows(r.data || []);
      setLastFetch(new Date());
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load breaches"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const acknowledge = useCallback(async (b) => {
    try {
      await api.post(`/geofence/breaches/${b.id}/acknowledge`);
      toast.success("Acknowledged");
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to acknowledge"));
    }
  }, [load]);

  // Options for the entity dropdowns — derived from the current rows so
  // the caller only sees selectable values that actually appear.
  const guardOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) {
      if (r.guard_id && !seen.has(r.guard_id)) {
        seen.set(r.guard_id, r.guard_name || r.badge_no || r.guard_id);
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const clientOptions = useMemo(() => {
    const names = new Set();
    for (const r of rows) if (r.client_name) names.add(r.client_name);
    return Array.from(names).sort();
  }, [rows]);
  const siteOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) {
      if (r.site_id && !seen.has(r.site_id)) {
        seen.set(r.site_id, r.site_name || r.site_id);
      }
    }
    // If a client is picked, only surface sites belonging to that client.
    let entries = Array.from(seen, ([id, name]) => {
      const example = rows.find((r) => r.site_id === id);
      return { id, name, client_name: example?.client_name };
    });
    if (clientFilter !== "all") entries = entries.filter((s) => s.client_name === clientFilter);
    return entries.sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, clientFilter]);

  // Reset dependents when a parent filter changes so an orphan pick can't
  // silently return zero rows.
  const handleClientChange = (v) => {
    setClientFilter(v);
    setSiteFilter("all");
  };

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (guardFilter !== "all" && r.guard_id !== guardFilter) return false;
      if (clientFilter !== "all" && r.client_name !== clientFilter) return false;
      if (siteFilter !== "all" && r.site_id !== siteFilter) return false;
      return true;
    });
  }, [rows, guardFilter, clientFilter, siteFilter]);

  const sortAccessors = useMemo(() => ({
    guard: (r) => (r.guard_name || "").toLowerCase(),
    site: (r) => (r.site_name || "").toLowerCase(),
    client: (r) => (r.client_name || "").toLowerCase(),
    started_at: (r) => r.started_at || "",
    max_distance_m: (r) => Number(r.max_distance_m) || 0,
    status: (r) => r.status || "",
  }), []);
  const sortState = useSortableRows(filteredRows, sortAccessors, { defaultKey: "started_at", defaultDir: "desc", storageKey: "geofence-breaches" });

  const summary = useMemo(() => {
    const open = filteredRows.filter((r) => r.status === "open").length;
    const closed = filteredRows.length - open;
    return { open, closed };
  }, [filteredRows]);

  const anyEntityFilterActive = guardFilter !== "all" || clientFilter !== "all" || siteFilter !== "all";
  const resetEntityFilters = () => {
    setGuardFilter("all");
    setClientFilter("all");
    setSiteFilter("all");
  };

  // Download the currently-filtered breach list as CSV. Applies over
  // `sortState.sorted` so the export honours whatever ordering the user
  // set on the table — one-click end-of-week compliance report.
  const exportCsv = useCallback(() => {
    const cols = [
      { key: "guard_name",       label: "Officer" },
      { key: "badge_no",         label: "Badge" },
      { key: "site_name",        label: "Site" },
      { key: "client_name",      label: "Client" },
      { key: "started_at",       label: "Started (UTC)" },
      { key: "ended_at",         label: "Ended (UTC)" },
      { key: "_duration",        label: "Duration" },
      { key: "max_distance_m",   label: "Max distance (m)" },
      { key: "fence_m",          label: "Fence (m)" },
      { key: "status",           label: "Status" },
      { key: "acknowledged_by",  label: "Ack by" },
      { key: "acknowledged_at",  label: "Ack at (UTC)" },
    ];
    const rowsForCsv = sortState.sorted.map((r) => ({
      ...r,
      _duration: fmtDuration(r.duration_s),
    }));
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v);
      // RFC-4180 quoting when the value contains delimiters or newlines.
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.map((c) => c.label).join(",");
    const body = rowsForCsv
      .map((r) => cols.map((c) => esc(r[c.key])).join(","))
      .join("\n");
    const blob = new Blob([`\uFEFF${header}\n${body}\n`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    const scopeBits = [
      guardFilter !== "all" && "guard",
      clientFilter !== "all" && "client",
      siteFilter !== "all" && "site",
      statusFilter !== "all" && statusFilter,
    ].filter(Boolean);
    const suffix = scopeBits.length ? `_${scopeBits.join("-")}` : "";
    a.download = `geofence-breaches_${stamp}${suffix}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rowsForCsv.length} row${rowsForCsv.length === 1 ? "" : "s"}`);
  }, [sortState.sorted, guardFilter, clientFilter, siteFilter, statusFilter]);

  return (
    <div data-testid="geofence-page">
      <PageHeader
        title="Geofence Breaches"
        subtitle="Officers who left the site geofence while clocked in. Reports auto-close when the  returns."
        action={
          <div className="inline-flex items-center gap-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1" data-testid="geofence-refresh-indicator">
              <RefreshCw className="size-3" /> {lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : "Loading…"}
            </div>
            <GhostButton
              data-testid="geofence-export-csv"
              onClick={exportCsv}
              disabled={sortState.sorted.length === 0}
              title={sortState.sorted.length === 0 ? "Nothing to export in the current filter" : "Download the currently-filtered breaches as CSV"}
            >
              <Download className="size-3" /> Export CSV
            </GhostButton>
            <GhostButton data-testid="geofence-refresh-button" onClick={load}>Refresh</GhostButton>
          </div>
        }
      />
      <div className="p-8 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="border border-[#27272A] bg-[#121212] px-4 py-2" data-testid="geofence-stat-open">
            <div className="label-mute">Open</div>
            <div className="font-display text-2xl font-bold text-[#FF3B30]">{summary.open}</div>
          </div>
          <div className="border border-[#27272A] bg-[#121212] px-4 py-2" data-testid="geofence-stat-closed">
            <div className="label-mute">Closed</div>
            <div className="font-display text-2xl font-bold text-zinc-400">{summary.closed}</div>
          </div>
          <div className="ml-auto flex items-center gap-1">
            {["all", "open", "closed"].map((k) => (
              <button
                key={k}
                data-testid={`geofence-filter-${k}`}
                onClick={() => setStatusFilter(k)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-widest border ${statusFilter === k ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "border-[#27272A] text-zinc-400 hover:text-zinc-200"}`}
              >{k}</button>
            ))}
          </div>
        </div>

        {/* Entity filters — Officer · Client · Site */}
        <div
          className="flex flex-wrap items-center gap-3 border border-[#27272A] bg-[#121212] px-4 py-3"
          data-testid="geofence-entity-filters"
        >
          <FilterSelect
            testid="geofence-guard-filter"
            label="Officer"
            value={guardFilter}
            onChange={setGuardFilter}
            options={[
              { value: "all", label: "All guards" },
              ...guardOptions.map((g) => ({ value: g.id, label: g.name })),
            ]}
          />
          <FilterSelect
            testid="geofence-client-filter"
            label="Client"
            value={clientFilter}
            onChange={handleClientChange}
            options={[
              { value: "all", label: "All clients" },
              ...clientOptions.map((n) => ({ value: n, label: n })),
            ]}
          />
          <FilterSelect
            testid="geofence-site-filter"
            label="Site"
            value={siteFilter}
            onChange={setSiteFilter}
            options={[
              { value: "all", label: siteOptions.length ? "All sites" : "No sites" },
              ...siteOptions.map((s) => ({ value: s.id, label: s.name, sub: clientFilter === "all" ? s.client_name : undefined })),
            ]}
            disabled={siteOptions.length === 0}
          />
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono tabular-nums">
            Showing {filteredRows.length} of {rows.length}
          </div>
          {anyEntityFilterActive && (
            <button
              type="button"
              onClick={resetEntityFilters}
              className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
              data-testid="geofence-entity-reset"
            >
              Reset filters
            </button>
          )}
        </div>

        <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
          <table className="w-full text-sm" data-testid="geofence-table">
            <thead className="border-b border-[#27272A]">
              <tr>
                <SortableHeader k="guard" state={sortState}>Officer</SortableHeader>
                <SortableHeader k="site" state={sortState}>Site</SortableHeader>
                <SortableHeader k="client" state={sortState}>Client</SortableHeader>
                <SortableHeader k="started_at" state={sortState}>Started</SortableHeader>
                <th className="px-4 py-3 text-left"><div className="label-mute">Duration</div></th>
                <SortableHeader k="max_distance_m" state={sortState}>Max distance</SortableHeader>
                <SortableHeader k="status" state={sortState}>Status</SortableHeader>
                <th className="px-4 py-3 text-right"><div className="label-mute">Actions</div></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {loading && <tr><td colSpan={8} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>}
              {!loading && sortState.sorted.length === 0 && (
                <tr><td colSpan={8} className="text-center py-16 text-zinc-500 scanlines">All clear — no geofence breaches recorded.</td></tr>
              )}
              {sortState.sorted.map((b) => {
                const isOpen = b.status === "open";
                const mapLink = `https://www.google.com/maps/search/?api=1&query=${b.last_lat ?? b.started_lat},${b.last_lng ?? b.started_lng}`;
                return (
                  <tr key={b.id} className="hover:bg-[#1c1c1e]" data-testid={`geofence-row-${b.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-display font-bold uppercase tracking-tight">{b.guard_name || "—"}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{b.badge_no || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{b.site_name || "—"}</td>
                    <td className="px-4 py-3 text-zinc-400">{b.client_name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-zinc-400">
                      {new Date(b.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{relTime(b.started_at)}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-zinc-300">{fmtDuration(b.duration_s)}</td>
                    <td className="px-4 py-3">
                      <PillBadge tone={b.max_distance_m > 500 ? "red" : "amber"}>
                        <MapPin className="size-3 mr-1" /> {b.max_distance_m}m
                      </PillBadge>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">fence {b.fence_m}m</div>
                    </td>
                    <td className="px-4 py-3">
                      {isOpen ? (
                        <PillBadge tone="red" data-testid={`geofence-status-${b.id}`}>OPEN</PillBadge>
                      ) : (
                        <PillBadge tone="default" data-testid={`geofence-status-${b.id}`}>closed</PillBadge>
                      )}
                      {b.acknowledged_at && (
                        <div className="text-[10px] uppercase tracking-widest text-[#34C759] mt-1 flex items-center gap-1">
                          <Check className="size-3" /> ack by {b.acknowledged_by}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <a
                          href={mapLink}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 border border-[#27272A] text-[10px] uppercase tracking-widest text-zinc-300 hover:text-white hover:border-[#0A84FF]"
                          data-testid={`geofence-map-${b.id}`}
                        >
                          <ExternalLink className="size-3" /> Map
                        </a>
                        {isOpen && (
                          <GhostButton
                            data-testid={`geofence-dispatch-${b.id}`}
                            onClick={() => setDispatchFor(b)}
                            title={`Send a dispatch blast to guards assigned to ${b.site_name}`}
                          >
                            <Radio className="size-3" /> Dispatch
                          </GhostButton>
                        )}
                        {!b.acknowledged_at && (
                          <GhostButton
                            data-testid={`geofence-ack-${b.id}`}
                            onClick={() => acknowledge(b)}
                            title="Mark as reviewed"
                          >
                            <Check className="size-3" /> Ack
                          </GhostButton>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {dispatchFor && (
        <NewDispatchModal
          onClose={() => setDispatchFor(null)}
          onSent={() => setDispatchFor(null)}
          restrictToSite={{ id: dispatchFor.site_id, name: dispatchFor.site_name }}
          prefill={{
            subject: `Geofence breach · ${dispatchFor.guard_name} @ ${dispatchFor.site_name}`,
            message: `${dispatchFor.guard_name} left the ${dispatchFor.site_name} geofence — currently ${dispatchFor.max_distance_m}m from site (fence ${dispatchFor.fence_m}m). Please investigate and make contact.`,
          }}
        />
      )}
    </div>
  );
}

/**
 * FilterSelect — small labelled <select> shared by the entity filter bar.
 * Mirrors the pattern used on SitePerformanceCard so the app has a
 * consistent filter-chip look across dashboards.
 */
function FilterSelect({ testid, label, value, onChange, options, disabled }) {
  return (
    <label className="inline-flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <select
        data-testid={testid}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`bg-[#0A0A0A] border border-[#27272A] text-xs text-zinc-200 px-2 py-1 h-[28px] max-w-[220px] focus:outline-none focus:border-[#FF3B30] ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}{o.sub ? ` — ${o.sub}` : ""}
          </option>
        ))}
      </select>
    </label>
  );
}
